import State from 'store/State';
import { selectOrders, selectOrdersState } from 'app/store/orders/selectors';
import { PortfolioInput } from 'portfolio/PortfolioInput';
import { AnyAction } from 'redux';
import { blotterComponentsSetSelectedRowIdsAction } from './store/components/actions';
import { PortfolioItem } from 'portfolio/PortfolioItem';
import { mapOrdersToPortfolioItems } from 'app/store/orders/mapping';
import {
    selectBlotterFilteredOrdersResults,
    toOrderResults,
} from './store/filteredOrders/selectors';
import { getPortfolioColumns } from 'app/store/orders/portfolioColumns';
import Order from 'communication/types/order/Order';
import {
    buildPortfolioSelection,
    PortfolioSelection,
} from 'portfolio/PortfolioSelection';
import { selectBlotterSelectedRowIds } from 'widgets/blotter/store/components/selectors';
import {
    selectAppSettings,
    useSelectAppSettings,
} from 'app/store/appSettings/selectors';
import ColumnPortfolio, {
    buildColumnDefinitions,
    buildColumnPortfolio,
} from 'widgets/blotter/columns/ColumnPortfolio';
import { selectBlotterConfiguration } from 'widgets/blotter/store/configuration/selectors';
import { IndigoDispatch } from 'store/store';
import { FilterValueType, MembershipSign } from 'utils/filter/filter';
import { buildUpdateBlotterFilterAction } from 'widgets/store/grid/filters/dispatcher';
import { WidgetType } from 'widgets/WidgetType';
import { isColumnFilter } from 'widgets/store/grid/filters/FilterState';
import { useWidgetContext } from 'widgets/WidgetContext';
import { useAppSelector } from 'store/hooks';
import { useMemo } from 'react';

/*
 * Returns a PortfolioInput implementation for a Blotter widget
 */
export function getBlotterPortfolioInput(widgetKey: string): PortfolioInput {
    return {
        getClearSelectionAction,
        getFilteredOrders,
        getFilteredPortfolio,
        usesOrdersStream: () => true,
        getPortfolioColumns,
        getSelection,
        updateActiveFilter,
    };

    function getClearSelectionAction(): AnyAction {
        return blotterComponentsSetSelectedRowIdsAction(widgetKey, []);
    }

    function getSelection(state: State): PortfolioSelection {
        return getBlotterPortfolioSelection(widgetKey, state);
    }

    /*
     * The filtered orders are build from the orders stream and filter results state
     */
    function getFilteredOrders(state: State): Order[] {
        const { data: allOrders } = selectOrdersState(state);
        const filterResults = selectBlotterFilteredOrdersResults(
            state,
            widgetKey
        );

        const orders = toOrderResults(allOrders, filterResults);

        return orders;
    }

    function getFilteredPortfolio(state: State): PortfolioItem[] {
        const orders = getFilteredOrders(state);

        return mapOrdersToPortfolioItems(state, orders);
    }

    function updateActiveFilter(
        state: State,
        dispatch: IndigoDispatch,
        fieldName: string,
        values: string[],
        exclude: boolean
    ) {
        const { symbology } = selectAppSettings(state);
        const columnPortfolio = buildColumnPortfolio(symbology);
        const colDefs = buildColumnDefinitions(columnPortfolio);

        const { filters } = selectBlotterConfiguration(
            state,
            widgetKey,
            columnPortfolio
        );

        const sign: MembershipSign = exclude
            ? MembershipSign.EXCLUDE
            : MembershipSign.INCLUDE;
        const column = fieldName as keyof ColumnPortfolio;

        // We replace any existing filter on this column...
        const nextFilters = filters.filter(
            (f) => !isColumnFilter(f) || f.column !== column
        );

        nextFilters.push({
            column,
            filter: {
                membershipSign: sign,
                valueType: colDefs[column].valueType as FilterValueType,
                values,
            },
        });

        const action = buildUpdateBlotterFilterAction(
            WidgetType.BLOTTER,
            widgetKey,
            nextFilters
        );
        dispatch(action);
    }
}

/*
 * The selection is built from the orders selected in the grid
 */
export function getBlotterPortfolioSelection(
    widgetKey: string,
    state: State
): PortfolioSelection {
    const indexes = selectBlotterSelectedRowIds(widgetKey, state);
    const selectedOrders = selectOrders(state, indexes);
    const { symbology, combineSellsAndShorts } = selectAppSettings(state);
    return buildPortfolioSelection({
        selectedOrders,
        symbology,
        combineSellsAndShorts,
    });
}

export function useBlotterSelection() {
    const { widgetKey } = useWidgetContext();
    const indexes = useAppSelector((state) =>
        selectBlotterSelectedRowIds(widgetKey, state)
    );
    const selectedOrders = useAppSelector((state) =>
        selectOrders(state, indexes)
    );
    const { symbology, combineSellsAndShorts } = useSelectAppSettings();

    return useMemo(
        () =>
            buildPortfolioSelection({
                selectedOrders,
                symbology,
                combineSellsAndShorts,
            }),
        [selectedOrders, symbology, combineSellsAndShorts]
    );
}
